import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { sa } from './ui';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export const AuditLog = ({ companies }) => {
  const [audit, setAudit] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [rateEvents, setRateEvents] = useState([]);
  const [open, setOpen] = useState(null);
  const nameOf = (id) => companies.find((c) => c.id === id)?.name || (id ? id.slice(0, 8) : '—');

  useEffect(() => {
    axios.get('/superadmin/audit?limit=200').then((r) => setAudit(r.data)).catch(() => {});
    axios.get('/superadmin/impersonation-sessions').then((r) => setSessions(r.data)).catch(() => {});
    axios.get('/superadmin/rate-limit-events?limit=100').then((r) => setRateEvents(r.data)).catch(() => {});
  }, []);

  const openSession = async (s) => {
    if (open?.id === s.id) return setOpen(null);
    const { data } = await axios.get(`/superadmin/impersonation-sessions/${s.id}`);
    setOpen(data);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 24 }} data-testid="audit-log">
      <section style={{ ...sa.surface, padding: 0, gridColumn: '1 / -1' }} data-testid="rate-limit-events">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #27272A', display: 'flex', justifyContent: 'space-between' }}>
          <p style={{ ...sa.overline, margin: 0 }}>Rate-limit events (public storefront traffic, per company)</p>
          <span style={{ color: '#A1A1AA', fontSize: 12 }}>{rateEvents.length} recent</span>
        </div>
        {rateEvents.length === 0 && <p style={{ padding: 16, color: '#A1A1AA', fontSize: 13 }}>No rate-limit events recorded.</p>}
        {rateEvents.length > 0 && (
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: '#0A0A0A' }}>{['When', 'Company', 'IP', 'Bucket', 'Scope', 'Path', 'Hits'].map((h) => <th key={h} style={{ ...sa.overline, textAlign: 'left', padding: '8px 12px' }}>{h}</th>)}</tr></thead>
              <tbody>
                {rateEvents.map((e) => (
                  <tr key={e.id} data-testid={`rate-event-${e.id}`} style={{ borderTop: '1px solid #1f1f22' }}>
                    <td style={{ padding: '8px 12px', color: '#A1A1AA' }}>{fmt(e.at)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{e.company_name}</td>
                    <td style={{ padding: '8px 12px' }}>{e.ip}</td>
                    <td style={{ padding: '8px 12px', color: '#fbbf24' }}>{e.bucket}</td>
                    <td style={{ padding: '8px 12px' }}>{e.scope}</td>
                    <td style={{ padding: '8px 12px', color: '#A1A1AA', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.method} {e.path}</td>
                    <td style={{ padding: '8px 12px' }}>{e.count}/{e.limit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section style={{ ...sa.surface, padding: 0 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #27272A' }}>
          <p style={{ ...sa.overline, margin: 0 }}>Impersonation sessions</p>
        </div>
        {sessions.length === 0 && <p style={{ padding: 20, color: '#A1A1AA', fontSize: 14 }}>No sessions yet.</p>}
        {sessions.map((s) => (
          <div key={s.id} data-testid={`session-row-${s.id}`} style={{ borderBottom: '1px solid #1f1f22' }}>
            <button type="button" onClick={() => openSession(s)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#fff', padding: '14px 20px', cursor: 'pointer', fontFamily: 'inherit' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <strong>{s.company_name}</strong>
                <span style={{ color: s.ended_at ? '#A1A1AA' : '#4ade80', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em' }}>{s.ended_at ? 'ENDED' : 'ACTIVE'}</span>
              </div>
              <div style={{ color: '#A1A1AA', fontSize: 12, marginTop: 4 }}>{s.superadmin_email} · {s.change_count || 0} changes</div>
              <div style={{ color: '#A1A1AA', fontSize: 12 }}>{fmt(s.started_at)} → {fmt(s.ended_at)}</div>
              <div style={{ fontSize: 13, marginTop: 6, color: '#e4e4e7' }}>“{s.reason}”</div>
            </button>
            {open?.id === s.id && (
              <div style={{ background: '#0A0A0A', padding: '12px 20px', fontSize: 12, maxHeight: 220, overflowY: 'auto' }} data-testid="session-changes">
                {(open.changes || []).length === 0 && <span style={{ color: '#A1A1AA' }}>No data changes during this session.</span>}
                {(open.changes || []).map((c, i) => (
                  <div key={i} style={{ padding: '4px 0', borderBottom: '1px dashed #1f1f22', display: 'flex', gap: 10 }}>
                    <span style={{ color: '#A1A1AA', minWidth: 70 }}>{new Date(c.at).toLocaleTimeString()}</span>
                    <span style={{ color: '#ff6b6b', fontWeight: 700 }}>{c.op}</span>
                    <span>{c.collection}</span>
                    <span style={{ color: '#A1A1AA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.filter}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>
      <section style={{ ...sa.surface, padding: 0 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #27272A' }}>
          <p style={{ ...sa.overline, margin: 0 }}>Super admin actions</p>
        </div>
        <div style={{ maxHeight: 640, overflowY: 'auto' }}>
          {audit.map((a) => (
            <div key={a.id} data-testid={`audit-row-${a.id}`} style={{ padding: '12px 20px', borderBottom: '1px solid #1f1f22', fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontWeight: 700, color: a.action.startsWith('impersonation') ? '#ff6b6b' : '#fff' }}>{a.action}</span>
                <span style={{ color: '#A1A1AA', fontSize: 12 }}>{fmt(a.at)}</span>
              </div>
              <div style={{ color: '#A1A1AA', fontSize: 12, marginTop: 3 }}>{a.actor_email} · {nameOf(a.company_id)}</div>
              {a.meta?.reason && <div style={{ fontSize: 12, marginTop: 3 }}>“{a.meta.reason}”</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
